import axios from 'axios';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config.js';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
  }
  return config;
});

// Recipe API endpoints
export const recipeAPI = {
  list: (params) => api.get('/recipes/', { params }),
  getById: (id) => api.get(`/recipes/${id}`),
  clone: (id) => api.post(`/recipes/${id}/clone`), // No data parameter needed
  getIngredients: (id) => api.get(`/recipes/${id}/ingredients`),
  myRecipes: () => api.get('/recipes/my-recipes'),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
};

// Ingredient API endpoints
export const ingredientAPI = {
  list: (params) => api.get('/ingredients/', { params }),
  getById: (id) => api.get(`/ingredients/${id}`),
  create: (data) => api.post('/ingredients/', data),
  update: (id, data) => api.put(`/ingredients/${id}`, data),
  delete: (id) => api.delete(`/ingredients/${id}`),
};

// Price API endpoints
export const priceAPI = {
  getHistory: (ingredientId, params) => 
    api.get(`/ingredients/${ingredientId}/prices`, { params }),
  getLatest: (ingredientId, region) => 
    api.get(`/ingredients/${ingredientId}/prices/latest`, { params: { region } }),
  addPrice: (ingredientId, data) => 
    api.post(`/ingredients/${ingredientId}/prices`, data),
};

// Menu API endpoints
export const menuAPI = {
  list: () => api.get('/menus/'),
  create: (data) => api.post('/menus/', data),
  getById: (id) => api.get(`/menus/${id}`),
  update: (id, data) => api.put(`/menus/${id}`, data),
  delete: (id) => api.delete(`/menus/${id}`),
  getShoppingList: (id, region) => 
    api.get(`/menus/${id}/shopping-list`, { params: { region } }),
};

// Pantry API endpoints
export const pantryAPI = {
  list: (params) => api.get('/pantry/', { params }),
  add: (data) => api.post('/pantry/', data),
  getById: (id) => api.get(`/pantry/${id}`),
  update: (id, data) => api.put(`/pantry/${id}`, data),
  delete: (id) => api.delete(`/pantry/${id}`),
  bulkConsume: (ingredients) => 
    api.post('/pantry/bulk-consume', { ingredients }),
};

// Categories API endpoints
export const categoryAPI = {
  list: () => api.get('/categories/'),
  getById: (id) => api.get(`/categories/${id}`),
};

// Auth API endpoints
export const authAPI = {
  getCurrentUser: () => api.get('/auth/me'),
};

// Promotion API endpoints (V43 - Upgraded)
export const getActivePromotion = async () => {
  try {
    const response = await api.get('/api/promotions/active');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch active promotion:', error);
    return null;
  }
};

// V43 - Get ALL promotions with pagination (admin only)
export const getAllPromotions = async (page = 1, limit = 10) => {
  const response = await api.get(`/api/promotions/?page=${page}&limit=${limit}`);
  return response.data;
};

// V43 - Create promotion (không deactivate promotion cũ)
export const createPromotion = async (data) => {
  const response = await api.post('/api/promotions/', data);
  return response.data;
};

// V43 - Update promotion
export const updatePromotion = async (id, data) => {
  const response = await api.put(`/api/promotions/${id}`, data);
  return response.data;
};

// V43 - Toggle promotion (chỉ toggle is_active)
export const togglePromotion = async (id) => {
  const response = await api.put(`/api/promotions/${id}/toggle`);
  return response.data;
};

// V43 - Save As (copy promotion)
export const saveAsPromotion = async (id, newName) => {
  const formData = new FormData();
  formData.append('new_name', newName);
  
  const response = await api.post(`/api/promotions/${id}/save-as`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

// V43 - Delete promotion (soft delete)
export const deletePromotion = async (id) => {
  const response = await api.delete(`/api/promotions/${id}`);
  return response.data;
};

// V43 - Upload image (lưu vào media_library)
export const uploadPromotionImage = async (file, type, category = 'promotion') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  formData.append('category', category);
  
  const response = await api.post('/api/promotions/upload-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

// V43 - Get media library with filters
export const getMediaLibrary = async (type = null, category = null, page = 1, limit = 50) => {
  let url = `/api/promotions/media-library?page=${page}&limit=${limit}`;
  if (type) url += `&file_type=${type}`;
  if (category) url += `&category=${category}`;
  
  const response = await api.get(url);
  return response.data;
};

export default api;
